import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import React from 'react';

import type { SafeZoneSelection } from '@src/safe-zones/types';

const logger = LogManager.getLogger('safe-zones:OverlayErrorBoundary');

interface Props {
  selection: SafeZoneSelection;
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Catches render errors thrown by a per-platform overlay component so a
 * single buggy overlay can't crash the entire clip-preview surface.
 *
 * On error: logs with full context, then renders nothing. The dropdown stays
 * functional; user can switch to a different overlay or `None`.
 */
export class SafeZoneOverlayErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo): void {
    logger.error('Overlay component threw during render', {
      selection: this.props.selection,
      ...extractErrorInfo(error),
      componentStack: info.componentStack,
    });
  }

  componentDidUpdate(prevProps: Props): void {
    if (prevProps.selection !== this.props.selection && this.state.hasError) {
      // Reset on selection change so a different overlay can attempt to render.
      this.setState({ hasError: false });
    }
  }

  render(): React.ReactNode {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}
